import {
  STANDARD_POINTS,
  THREE_ONE_ZERO,
  engineIds,
  getEngine,
  parseDescriptor,
  TIEBREAKS,
  TIEBREAK_PRESETS,
  type PairingSystemId,
  type PointSystem,
  type TournamentSettings,
} from '@ctd/core';

export type PointSystemId = 'standard' | '3-1-0';

/** The values of the setup form, all as the form holds them (strings). */
export interface SettingsForm {
  name: string;
  city: string;
  startDate: string;
  endDate: string;
  chiefArbiter: string;
  rounds: string;
  system: string;
  pointSystem: PointSystemId | 'custom';
  initialColour: 'w' | 'b';
  acceleration: 'none' | 'baku';
  tiebreaks: string;
}

export const DEFAULT_TIEBREAKS = 'BH/C1, BH, SB, WIN';

/** Named tiebreak lists from core (common practice, not FIDE recommendations). */
export function tiebreakPresets(): { name: string; text: string }[] {
  return Object.entries(TIEBREAK_PRESETS).map(([name, list]) => ({ name, text: list.join(', ') }));
}

/** Every tiebreak code core knows, with its name. */
export function tiebreakCatalogue(): { code: string; name: string }[] {
  return Object.entries(TIEBREAKS)
    .filter(([code]) => code !== 'PTS')
    .map(([code, t]) => ({ code, name: t.name }));
}

export interface EngineOption {
  readonly id: string;
  readonly name: string;
}

/** Pairing systems that have an engine, with their display names. */
export function engineOptions(): EngineOption[] {
  return engineIds().map((id) => {
    try {
      return { id, name: getEngine(id).name };
    } catch {
      return { id, name: id };
    }
  });
}

export function defaultSystem(): string {
  const ids = engineIds();
  return ids.includes('dutch') ? 'dutch' : (ids[0] ?? 'monrad');
}

export function emptySettingsForm(): SettingsForm {
  return {
    name: '',
    city: '',
    startDate: '',
    endDate: '',
    chiefArbiter: '',
    rounds: '5',
    system: defaultSystem(),
    pointSystem: 'standard',
    initialColour: 'w',
    acceleration: 'none',
    tiebreaks: DEFAULT_TIEBREAKS,
  };
}

function samePoints(a: PointSystem, b: PointSystem): boolean {
  return (Object.keys(a) as (keyof PointSystem)[]).every((k) => a[k] === b[k]);
}

export function pointSystemId(ps: PointSystem): PointSystemId | 'custom' {
  if (samePoints(ps, STANDARD_POINTS)) return 'standard';
  if (samePoints(ps, THREE_ONE_ZERO)) return '3-1-0';
  return 'custom';
}

export function formFromSettings(s: TournamentSettings): SettingsForm {
  return {
    name: s.name,
    city: s.city,
    startDate: s.startDate,
    endDate: s.endDate,
    chiefArbiter: s.chiefArbiter,
    rounds: String(s.rounds),
    system: s.system,
    pointSystem: pointSystemId(s.pointSystem),
    initialColour: s.initialColour,
    acceleration: s.acceleration,
    tiebreaks: s.tiebreaks.join(', '),
  };
}

export interface TiebreakParse {
  readonly tiebreaks: string[];
  readonly errors: string[];
}

/**
 * Read a comma-separated tiebreak list ("BH/C1, BH, SB, WIN"; the older
 * "BH:C1" form works too), checking each code and returning the canonical
 * MTB26 descriptors.
 */
export function parseTiebreakList(text: string): TiebreakParse {
  const tiebreaks: string[] = [];
  const errors: string[] = [];
  for (const part of text.split(/[,;\n]/)) {
    const t = part.trim();
    if (t === '') continue;
    try {
      tiebreaks.push(parseDescriptor(t).text);
    } catch (e) {
      errors.push(e instanceof Error ? e.message : String(e));
    }
  }
  return { tiebreaks, errors };
}

/** A readable name for a tiebreak code, e.g. "BH/C1" → "Buchholz, cut 1". */
export function tiebreakLabel(code: string): string {
  try {
    const d = parseDescriptor(code);
    const parts = [TIEBREAKS[d.name]?.name ?? d.name];
    if (d.cutHigh > 0) parts.push(`median ${d.cutHigh}`);
    else if (d.cutLow > 0) parts.push(`cut ${d.cutLow}`);
    if (d.forfeitsPlayed) parts.push('forfeits as played');
    if (d.reverse) parts.push('reversed');
    return parts.join(', ');
  } catch {
    return code;
  }
}

export type FormErrors = Partial<Record<keyof SettingsForm, string>>;

export interface SettingsResult {
  readonly settings: TournamentSettings | null;
  readonly errors: FormErrors;
}

/**
 * Turn the form into settings. `base` supplies the fields the form does not
 * show (and the point system when the form says "custom"); `minRounds` is the
 * number of rounds already paired.
 */
export function settingsFromForm(
  form: SettingsForm,
  base?: TournamentSettings,
  minRounds = 0,
): SettingsResult {
  const errors: FormErrors = {};
  const name = form.name.trim();
  if (name === '') errors.name = 'Enter the tournament name.';
  const rounds = Number(form.rounds);
  if (!Number.isInteger(rounds) || rounds < 1 || rounds > 99) {
    errors.rounds = 'Enter a whole number of rounds from 1 to 99.';
  } else if (rounds < minRounds) {
    errors.rounds = `${minRounds} rounds are already paired; unpair rounds first.`;
  }
  if (form.startDate !== '' && form.endDate !== '' && form.endDate < form.startDate) {
    errors.endDate = 'The end date is before the start date.';
  }
  const tb = parseTiebreakList(form.tiebreaks);
  if (tb.errors.length > 0) errors.tiebreaks = tb.errors.join('; ');
  if (!engineIds().includes(form.system) && form.system !== base?.system) {
    errors.system = 'Choose a pairing system.';
  }
  if (Object.keys(errors).length > 0) return { settings: null, errors };
  const pointSystem =
    form.pointSystem === '3-1-0'
      ? THREE_ONE_ZERO
      : form.pointSystem === 'custom' && base !== undefined
        ? base.pointSystem
        : STANDARD_POINTS;
  return {
    settings: {
      federation: '',
      deputyArbiters: [],
      timeControl: '',
      ...base,
      name,
      city: form.city.trim(),
      startDate: form.startDate,
      endDate: form.endDate,
      chiefArbiter: form.chiefArbiter.trim(),
      rounds,
      system: form.system as PairingSystemId,
      pointSystem,
      initialColour: form.initialColour,
      acceleration: form.acceleration,
      tiebreaks: tb.tiebreaks,
    },
    errors,
  };
}

/** The fields that differ between two settings objects (for SettingsChanged). */
export function settingsPatch(
  before: TournamentSettings,
  after: TournamentSettings,
): Partial<TournamentSettings> {
  const patch: Record<string, unknown> = {};
  for (const k of Object.keys(after) as (keyof TournamentSettings)[]) {
    if (JSON.stringify(before[k]) !== JSON.stringify(after[k])) patch[k] = after[k];
  }
  return patch as Partial<TournamentSettings>;
}
